// A DEPOSIT'S WORDS AND ITS ONE BUTTON (UI rebuild batch 4, custody hand-off). Copied from screens/custody/Deposits.tsx
// (not exported there): the rating's result words, the step line with its times, the sweep's card state as words, the
// waits-on-him test, the deep link to a ready card — and StartSigning, the button that opens a 30-minute window so the
// desk builds the cards, following the window as the desk holds it. The words are the lane's; nothing is decided here.
import { SigninQuiet } from './SigninLine';
import { isCustodySignin } from '../../../lib/errors';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { custody as api, type DepositRow } from '../../../lib/api';
import { POOL_WORD, CLASS_WORD } from '../../../lib/custody-format';
import { cardStateOf, windowOpenUntil, type CardState } from '../../../lib/custody-screen';
import { StateLine, Pending } from '../kit';
import { clock } from './parts';

const textOf = (err: unknown): string => String((err as { message?: unknown } | null)?.message ?? err);

/** The rating's result as said (N-011, 20 Oct 2026): where the money went. Older rows keep their old words. */
export const RESULT_WORD: Record<string, string> = {
  clean: `clean → ${CLASS_WORD['running_hot']}`,
  exposed: `→ ${CLASS_WORD['dirty_hot']}`,
  pending: 'not rated yet',
};

/** KYT's level, or a look-alike sender, says other than clean (the CC, 3 Nov 2026: "green clean → Hot beside red"): then
 *  no deposit word says clean or green — KYT's level and the route stand as two facts (the KYT block; Steps' "Routed to"). */
export const kytNotClean = (d: DepositRow): boolean => {
  const v = d.kyt?.view;
  return !!v && (!!v.poisoning || (!!v.level && v.level !== 'clean'));
};

/** The status's own line (Deposits.tsx STATUS line / more). */
export const STATUS_LINE: Partial<Record<DepositRow['status'], { line: string; tone: 'warn' | 'bad'; more?: string }>> = {
  held: { line: 'Not rated yet — asked again every minute.', tone: 'warn', more: 'KYT has not rated this payment yet. It is not accepted until it is.' },
  quarantined: { line: 'Held in the Flagged wallet by your decision.', tone: 'bad', more: 'Never Hot. It moves only on your word.' },
  rejected: { line: 'Did not succeed on-chain — nothing credited.', tone: 'bad' },
};

/** The step line for a sweep at its card, by the card's state. */
export function cardStep(state: CardState | null, builtAt: string | null): string {
  switch (state) {
    case 'open': return `Card ready ${clock(builtAt)} — sign it in Approvals`;
    case 'signed': return 'Signed — the desk sends it shortly';
    case 'expired': return 'Card lapsed — rebuilt when you start signing';
    case 'replaced': return 'Card replaced — the desk builds a new one';
    default: return 'No card yet — the desk builds it';
  }
}

/** Every step the money took, each with its time (the operator, 21 Oct 2026); the one it waits on is named. */
export function stepsOf(d: DepositRow): string[] {
  const steps: string[] = [`Seen ${clock(d.detectedAt)}`];
  if (d.confirmedAt) steps.push(`Confirmed ${clock(d.confirmedAt)}`);
  if (d.heldAt && d.status === 'held') steps.push(`Held ${clock(d.heldAt)}`);
  if (d.kytRatedAt || d.screenedAt) steps.push(`Screened ${d.kytVerdict && !kytNotClean(d) ? (RESULT_WORD[d.kytVerdict] ?? d.kytVerdict) : ''} ${clock(d.kytRatedAt ?? d.screenedAt)}`.replace(/\s+/g, ' '));
  if (d.pool && ['routed', 'swept'].includes(d.status)) steps.push(`Routed to ${POOL_WORD[d.pool] ?? d.pool}`);
  return [...steps, ...sweepSteps(d.sweep)];
}

/**
 * The sweep's steps, in order; the last is where it stands NOW — shown on the deposit itself, not only in its history
 * (Screens and Astra on the operations screens, 27 Oct 2026, #7: a routed deposit whose sweep FAILED read "Routed").
 */
export function sweepSteps(sw: DepositRow['sweep']): string[] {
  if (!sw) return [];
  if (sw.status === 'queued') return ['Waiting for you to start signing'];
  if (sw.status === 'activating') return ['Preparing the address on-chain — about a minute'];
  if (sw.status === 'expired') return ['Card lapsed — rebuilt when you start signing'];
  if (sw.status === 'awaiting_approval') return [cardStep(cardStateOf(sw), sw.builtAt)];
  if (sw.status === 'energizing') return ['Signed — readying the fee, then sending'];
  if (sw.status === 'broadcast') return [`Sent ${clock(sw.broadcastAt)}`];
  if (sw.status === 'swept') return [`Sent ${clock(sw.broadcastAt)}`, `Done ${clock(sw.completedAt)}`];
  if (sw.status === 'failed') return ['Failed — needs a look'];
  return [];
}

/** Where a ready card is signed: its own review (#card-<txid> is answered by the review), or the list. */
export const cardPath = (sw: DepositRow['sweep']): string => {
  const txid = sw?.card?.txid ?? sw?.txHash ?? null;
  return txid ? `/custody/approvals/${txid}` : '/custody/approvals';
};

/** A sweep in motion on the desk's side: the list re-reads every 3 s while one is. */
export const IN_MOTION = new Set(['activating', 'energizing', 'broadcast']);

/** A routed deposit whose sweep waits for a window: nothing moves until he starts signing. */
export const waitsOnHim = (d: DepositRow) => d.status === 'routed' && (d.sweep?.status === 'queued' || d.sweep?.status === 'expired'
  || cardStateOf(d.sweep) === 'expired');

/**
 * The one button that starts a signature (Deposits.tsx StartSigning): it opens a 30-minute window; the desk wakes,
 * activates a fresh address when it must, builds the card and Approvals shows it. It follows the window as the desk
 * holds it: open, it waits; closed or lapsed, it is pressable again. A failed window read is said, never trusted.
 */
export function StartSigning({ demoted = false }: { demoted?: boolean }) {
  const qc = useQueryClient();
  const win = useQuery({ queryKey: ['custody', 'approval-window'], queryFn: api.approvalWindow, refetchInterval: 30_000, retry: false });
  const m = useMutation({
    mutationFn: () => api.openApprovalWindow(30),
    onSuccess: () => { for (const key of ['approval-window', 'approvals', 'deposits', 'summary']) void qc.invalidateQueries({ queryKey: ['custody', key] }); },
  });
  const read = win.isError ? 'failed' : win.data === undefined ? 'reading' : 'read';
  const openUntil = read === 'read' ? windowOpenUntil(win.data) : null;
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" className={demoted ? 'btn-secondary px-4 text-body' : 'btn-primary px-6'} disabled={m.isPending || openUntil !== null || read === 'reading'} onClick={() => m.mutate()}>
          {m.isPending ? 'Starting…' : openUntil ? 'Window open' : 'Start signing'}
        </button>
        <span className="text-meta text-tg-hint">{read === 'reading' ? 'reading the window…' : openUntil ? `until ${clock(openUntil)}` : 'opens a 30-minute window'}</span>
      </div>
      {read === 'failed' && isCustodySignin(win.error) && <SigninQuiet error={win.error} />}
      {read === 'failed' && !isCustodySignin(win.error) && (
        <StateLine tone="bad" more={textOf(win.error)} action={<button type="button" className="btn-link text-body" onClick={() => void win.refetch()} disabled={win.isFetching}>Try again</button>}>
          Couldn&apos;t read the signing window.
        </StateLine>
      )}
      {openUntil && <Pending inline what="The desk is preparing the cards…" />}
      {m.isError && <StateLine tone="bad" more={textOf(m.error)}>Couldn&apos;t start — try again.</StateLine>}
    </div>
  );
}
